import './TicketPopup.scss';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import { createTicket, fetchAllTickets} from '../../../slices/TicketSlice';
import HelpIcon from '@mui/icons-material/Help';
import Tooltip from '@mui/material/Tooltip';
import { toast } from 'react-toastify';
 
/**
* TicketPopup Component
*
* @component
* @description
* A popup form for creating a new ticket. Allows users to enter a title and description,
* clear the form, and submit the data to the backend. Submission is attempted through 'fetch'
* first, and falls back to 'axios' if 'fetch' fails. On successful submission, navigates
* to the newly created ticket's detail page.
*
* Includes:
* - Controlled inputs for title and description.
* - Clear button to reset both fields.
* - Submit button to send data to the server.
* - Close icon to trigger the 'onClose' callback provided by the parent.
*
* @param {Object} props - Component props.
* @param {function} props.onClose - Callback function to close the popup.
*
* @returns {JSX.Element} A form-based popup for ticket creation.
*
* @example
* // Usage:
* <TicketPopup onClose={handleClosePopup} />
*/
 
 
 
export default function TicketPopup({ onClose }) {
  const dispatch = useDispatch();
 
  // Top level states
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const [locked, setLocked] = useState(false);
  const [disabled, setDisabled] = useState(true); // Submit initially disabled
 
  useEffect(() => {
  const titleValid = title.length >= 1 && title.length <= 50;
  const descriptionValid = description.length >= 1 && description.length <= 250;
 
  setDisabled(!(titleValid && descriptionValid && type !== '')); // submit button
}, [title, description, type]);
 
  // Close popup
  function handleClose() {
    console.log("Closing popup");
    onClose();
  }
 
  // Clear form
  function handleClear() {
    setTitle('');
    setDescription('');
    setType('');
  }
 
  // Submit button lock animation
  function handleClick() {
    setDisabled(true);
    setLocked(true);
 
    setTimeout(() => {
      setDisabled(false);
      setLocked(false);
    }, 2000);
  }
 
  // Form submit handler
  async function handleSubmit(e) {
    e.preventDefault();
    handleClick();
 
    if (title.length < 1 ){
    toast.error('Title needs to be at least 1 character');
    return;
    }
    if( description.length < 1) {
    toast.error('Description needs to be at least 1 character');
    return;
    } if( type === '') {
      toast.error('Please select a type');
    return;
    }
 
    const newTicket = {
      title,
      description,
      type,
    };
     try {
    setDisabled(true);
    setLocked(true);
    await dispatch(createTicket(newTicket)).unwrap();
    handleClear();
    onClose();
     } catch (err) {
    console.error("Error creating ticket:", err);
    toast.error("Failed to create ticket. Please try again.");
  } finally {
    // Always unlock UI
    await dispatch(fetchAllTickets()).unwrap();
    setDisabled(false);
    setLocked(false);
  }
}

  

  return (
    <>
      <div className="ticketPopupBackdrop" onClick={handleClose} data-testid="ticket-popup-backdrop" />
      {locked && <div className="screenBlocker" data-testid="screen-blocker" />}
      <main className="TicketPopup" data-testid="ticket-popup-main">
        <form className="content" onSubmit={handleSubmit} data-testid="ticket-popup-form">
          {/* Exit button */}
          <div className="exit">
            <CloseIcon className='exitButton' data-testid="close-button" onClick={handleClose} />
          </div>
 
          {/* Title container */}
          <div className="titleContainer">
            <span className="titleSpan">Title</span>
            <input
              type="text"
              data-testid="title-input"
              value={title}
              onChange={e => setTitle(e.target.value)}
              minLength={1}
              maxLength={50}
              placeholder="Title (1-50 characters)"
              className="title"
              required
            />
          </div>
 
          {/* Type selector */}
          <div className="typeContainer" data-testid="type-container">
            <span className="typeSpan">Type</span>
            <select
              value={type}
              onChange={e => setType(e.target.value)}
              data-testid="type-select"
              className="type"
            >
              <option value="" data-testid="select-type-empty-option">Select a type</option>
              <option value="TECHNOLOGY" data-testid="select-type-technology-option">Technology</option>
              <option value="STUDENT_SUPPORT" data-testid="select-type-student-support-option">Student Support</option>
            </select>
            <Tooltip title={<>Technology - Anything related to technology <br/>Student Suppport - Personal or Professional Help</>} placement="right" arrow enterDelay={500} enterNextDelay={500} data-testid="type-tooltip">
              <HelpIcon data-testid="help-icon" />
            </Tooltip>
          </div>
 
          {/* Description container */}
          <div className="descriptionContainer" data-testid="description-container">
            <span className="descriptionSpan">Description</span>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              minLength={1}
              maxLength={250}
              placeholder="Description (1-250 characters)"
              className="description"
              required
              data-testid="description-input"
            />
          </div>
 
          {/* Buttons container */}
          <div className="buttonContainer" data-testid="button-container">
            <Tooltip title="Resets all fields in this form" placement="top" arrow enterDelay={500} enterNextDelay={500} data-testid="clear-button-tooltip">
              <button
                type="button"
                className="clear button"
                onClick={handleClear}
                disabled={title.length === 0 && description.length === 0 && type.length === 0}
                data-testid="clear-button"
              >
                Clear
              </button>
            </Tooltip>
            
          <button
            type="submit"
            className="submit button"
            onClick={handleClick && handleSubmit}
            disabled={disabled}
            data-testid="submit-button"
          >
            {locked ? <span className="loadingSubmit" data-testid="loading-submit"></span> : "Submit"}
          </button>
        </div>
      </form>
    </main>
  </>
);
}